import React, { type ComponentProps } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { CustomNavigationHeader } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const PartnerLineItemNavigationHeader = (props: Props) => {
  const { closeAccessibilityLabel, onClose, scrollOffsetSV, title } = props;

  const { right: safeAreaRightInset, left: safeAreaLeftInset } = useSafeAreaInsets();

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = useStyle(
    () => ({ paddingRight: safeAreaRightInset, paddingLeft: safeAreaLeftInset }),
    [safeAreaLeftInset, safeAreaRightInset],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <CustomNavigationHeader.Container
      buttonsReservedSpace="left-and-right"
      palette="oatmeal"
      safeAreaEdges={SAFE_AREA_EDGES}
      scrollOffsetSV={scrollOffsetSV}
      style={containerDynamicStyles}
    >
      <CustomNavigationHeader.Content style={styles.content} scrollOffsetSV={scrollOffsetSV}>
        <CustomNavigationHeader.Text numberOfLines={1} style={styles.title} textAlignment="center">
          {title}
        </CustomNavigationHeader.Text>
      </CustomNavigationHeader.Content>

      {onClose ? (
        <CustomNavigationHeader.Button.Right
          accessibilityLabel={closeAccessibilityLabel}
          name="IconClose"
          onPress={onClose}
        />
      ) : null}
    </CustomNavigationHeader.Container>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES: ComponentProps<typeof CustomNavigationHeader.Container>['safeAreaEdges'] =
  Platform.OS === 'android' ? ['top'] : [];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    flex: 0,
    width: '100%',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  closeAccessibilityLabel?: string;
  onClose?: () => void;
  scrollOffsetSV: ComponentProps<typeof CustomNavigationHeader.Container>['scrollOffsetSV'];
  title: string;
};
